import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle, X, Send } from "lucide-react";
import { getSessionId } from "../../lib/analytics";

const API = process.env.REACT_APP_BACKEND_URL;
const GREETING = "Namaste. I am Ira, your virtual private luxury travel concierge. How may I help you craft your next mindful journey?";

const IRA_AVATAR = `${API}/api/assets/img/ira-avatar-sm.jpg`;

/* Turn site paths Ira mentions (e.g. /tours/mystical-bhutan) into links that
   open in a new tab. Trailing punctuation stays outside the link. */
const PATH_RE = /((?:https?:\/\/(?:www\.)?myecotour\.in)?\/(?:tours|honeymoons|destinations|experiences|retreats|contact|journal|about|impact|rewards)(?:\/[A-Za-z0-9-]+)*\/?)/g;

const renderWithLinks = (text) =>
    text.split(PATH_RE).map((part, i) => {
        if (i % 2 === 0) return part;
        const path = part.replace(/^https?:\/\/(?:www\.)?myecotour\.in/, "");
        return (
            <a
                key={i}
                href={path}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="ira-link"
                className="underline underline-offset-2 font-medium text-[#1E3B2C] hover:text-[#a5812f] transition-colors"
            >
                {part}
            </a>
        );
    });

const sessionId = () => {
    let id = localStorage.getItem("ira_session_id");
    if (!id) {
        id = `ira-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        localStorage.setItem("ira_session_id", id);
    }
    return id;
};

export default function IraChat() {
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([{ role: "assistant", text: GREETING }]);
    const [input, setInput] = useState("");
    const [streaming, setStreaming] = useState(false);
    const [leadOpen, setLeadOpen] = useState(false);
    const [lead, setLead] = useState({ name: "", email: "", phone: "" });
    const [leadSending, setLeadSending] = useState(false);
    const [leadSent, setLeadSent] = useState(false);
    const scrollRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, open]);

    const send = async () => {
        const text = input.trim();
        if (!text || streaming) return;
        setInput("");
        setMessages((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
        setStreaming(true);
        try {
            const res = await fetch(`${API}/api/ira/chat`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ session_id: sessionId(), message: text, visitor_session_id: getSessionId() }),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buf = "";
            for (;;) {
                const { done, value } = await reader.read();
                if (done) break;
                buf += decoder.decode(value, { stream: true });
                const parts = buf.split("\n\n");
                buf = parts.pop();
                for (const part of parts) {
                    const line = part.trim();
                    if (!line.startsWith("data:")) continue;
                    const payload = line.slice(5).trim();
                    if (payload === "[DONE]") continue;
                    try {
                        const { t } = JSON.parse(payload);
                        if (t) setMessages((m) => {
                            const copy = [...m];
                            copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + t };
                            return copy;
                        });
                    } catch { /* skip malformed chunk */ }
                }
            }
        } catch {
            setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = {
                    role: "assistant",
                    text: "My apologies — I seem to be having a quiet moment. Please use the Enquire Now button below and our concierge will come back to you in due course.",
                };
                return copy;
            });
        } finally {
            setStreaming(false);
        }
    };

    const sendToConcierge = async (e) => {
        e.preventDefault();
        if (leadSending) return;
        setLeadSending(true);
        const transcript = messages
            .slice(-24)
            .map((m) => `${m.role === "user" ? "Guest" : "Ira"}: ${m.text}`)
            .join("\n");
        try {
            await fetch(`${API}/api/enquiries`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    full_name: lead.name,
                    email: lead.email,
                    phone: lead.phone,
                    destination_interest: "Ira chat conversation",
                    message: `[Lead captured by Ira — chat transcript]\n\n${transcript}`.slice(0, 4000),
                    source_path: "ira-chat",
                }),
            }).then((r) => { if (!r.ok) throw new Error(r.status); });
            setLeadSent(true);
            setLeadOpen(false);
            setMessages((m) => [...m, {
                role: "assistant",
                text: `Thank you, ${lead.name.split(" ")[0]}. I have shared our conversation with our concierge team — they will come back to you in due course on ${lead.phone} or ${lead.email}. Namaste.`,
            }]);
        } catch {
            setMessages((m) => [...m, {
                role: "assistant",
                text: "My apologies — I could not reach the concierge desk just now. Please try again in a moment, or use the Enquire Now button below.",
            }]);
        } finally {
            setLeadSending(false);
        }
    };

    const hasConversation = messages.some((m) => m.role === "user");

    return (
        <>
            {/* Launcher */}
            {!open && (
                <button
                    type="button"
                    onClick={() => setOpen(true)}
                    data-testid="ira-launcher"
                    aria-label="Chat with Ira, our AI concierge"
                    className="fixed bottom-6 right-6 z-[80] flex items-center gap-2.5 pl-1.5 pr-5 py-1.5 rounded-full bg-[#1E3B2C] text-[#F5F1E8] shadow-xl hover:bg-[#2a5240] transition-colors"
                >
                    <img src={IRA_AVATAR} alt="Ira, your AI concierge" className="w-10 h-10 rounded-full object-cover border-2 border-[#e0c072]/70" />
                    <span className="text-sm font-medium tracking-wide">Ask Ira</span>
                </button>
            )}

            {/* Panel */}
            {open && (
                <div
                    data-testid="ira-panel"
                    className="fixed bottom-6 right-6 z-[80] w-[92vw] max-w-[390px] h-[560px] max-h-[78vh] flex flex-col bg-[#FCFAF5] border border-[#E5DFD0] shadow-2xl rounded-2xl overflow-hidden"
                >
                    <div className="flex items-center justify-between px-5 py-4 bg-[#1E3B2C] text-[#F5F1E8]">
                        <div className="flex items-center gap-3">
                            <img src={IRA_AVATAR} alt="Ira" className="w-11 h-11 rounded-full object-cover border-2 border-[#e0c072]/70" />
                            <div>
                                <p className="font-serif text-lg leading-none">Ira, MyEcoTour</p>
                                <p className="text-[11px] opacity-75 mt-1">Private Luxury Concierge · AI</p>
                            </div>
                        </div>
                        <button type="button" onClick={() => setOpen(false)} data-testid="ira-close-btn" aria-label="Close chat" className="p-1.5 hover:opacity-70 transition-opacity">
                            <X size={18} />
                        </button>
                    </div>

                    <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="ira-messages">
                        {messages.map((m, i) => (
                            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                                <div
                                    className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                                        m.role === "user"
                                            ? "bg-[#1E3B2C] text-[#F5F1E8] rounded-2xl rounded-br-sm"
                                            : "bg-white border border-[#E5DFD0] text-[#2b2b25] rounded-2xl rounded-bl-sm"
                                    }`}
                                    data-testid={`ira-msg-${m.role}-${i}`}
                                >
                                    {m.text ? (m.role === "assistant" ? renderWithLinks(m.text) : m.text) : (
                                        <span className="inline-flex gap-1 items-center py-1" aria-label="Ira is typing">
                                            <span className="w-1.5 h-1.5 rounded-full bg-[#1E3B2C]/40 animate-bounce" />
                                            <span className="w-1.5 h-1.5 rounded-full bg-[#1E3B2C]/40 animate-bounce [animation-delay:120ms]" />
                                            <span className="w-1.5 h-1.5 rounded-full bg-[#1E3B2C]/40 animate-bounce [animation-delay:240ms]" />
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="px-4 pb-2 space-y-2">
                        {hasConversation && !leadSent && !leadOpen && (
                            <button
                                type="button"
                                data-testid="ira-lead-btn"
                                onClick={() => setLeadOpen(true)}
                                className="w-full py-2.5 text-[10px] tracking-[0.2em] uppercase font-semibold bg-[#e0c072] text-[#1E3B2C] hover:bg-[#d4b25e] transition-colors"
                            >
                                Send this conversation to the concierge
                            </button>
                        )}
                        {leadOpen && (
                            <form onSubmit={sendToConcierge} className="border border-[#E5DFD0] bg-white p-3 space-y-2" data-testid="ira-lead-form">
                                <p className="text-[11px] text-[#6b6558]">Share your details and our concierge will pick up right where Ira left off.</p>
                                <input required minLength={2} maxLength={80} value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} placeholder="Your name" data-testid="ira-lead-name" className="w-full text-sm border border-[#E5DFD0] px-3 py-2 outline-none focus:border-[#1E3B2C]" />
                                <input required type="email" maxLength={120} value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} placeholder="Email address" data-testid="ira-lead-email" className="w-full text-sm border border-[#E5DFD0] px-3 py-2 outline-none focus:border-[#1E3B2C]" />
                                <input required minLength={7} maxLength={20} value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} placeholder="Phone / WhatsApp (with country code)" data-testid="ira-lead-phone" className="w-full text-sm border border-[#E5DFD0] px-3 py-2 outline-none focus:border-[#1E3B2C]" />
                                <div className="flex gap-2">
                                    <button type="submit" disabled={leadSending} data-testid="ira-lead-submit" className="flex-1 py-2.5 text-[10px] tracking-[0.2em] uppercase font-semibold bg-[#1E3B2C] text-[#F5F1E8] hover:bg-[#2a5240] disabled:opacity-50 transition-colors">
                                        {leadSending ? "Sending…" : "Send to concierge"}
                                    </button>
                                    <button type="button" onClick={() => setLeadOpen(false)} data-testid="ira-lead-cancel" className="px-4 py-2.5 text-[10px] tracking-[0.2em] uppercase font-semibold border border-[#E5DFD0] text-[#6b6558] hover:border-[#1E3B2C] transition-colors">
                                        Later
                                    </button>
                                </div>
                            </form>
                        )}
                        <button
                            type="button"
                            data-testid="ira-enquire-btn"
                            onClick={() => { setOpen(false); navigate("/contact"); }}
                            className="w-full py-2.5 text-[10px] tracking-[0.25em] uppercase font-semibold border border-[#1E3B2C]/25 text-[#1E3B2C] hover:bg-[#1E3B2C] hover:text-[#F5F1E8] transition-colors"
                        >
                            Enquire Now — concierge will respond
                        </button>
                    </div>

                    <form
                        onSubmit={(e) => { e.preventDefault(); send(); }}
                        className="flex items-center gap-2 px-4 py-3 border-t border-[#E5DFD0] bg-white"
                    >
                        <input
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder="Ask about journeys, honeymoons, prices…"
                            maxLength={1500}
                            data-testid="ira-input"
                            className="flex-1 text-sm bg-transparent outline-none placeholder:text-[#9a9384]"
                        />
                        <button
                            type="submit"
                            disabled={streaming || !input.trim()}
                            data-testid="ira-send-btn"
                            aria-label="Send message"
                            className="p-2.5 rounded-full bg-[#1E3B2C] text-[#F5F1E8] disabled:opacity-40 hover:bg-[#2a5240] transition-colors"
                        >
                            <Send size={15} />
                        </button>
                    </form>
                </div>
            )}
        </>
    );
}
